import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, X, Palette, ChevronDown, ChevronRight } from "lucide-react";

const STATUS_STYLE = {
  pending: "bg-zinc-100 text-zinc-700",
  approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-red-100 text-red-700",
};

export default function ColourManagement() {
  const [colours, setColours] = useState([]);
  const [labDips, setLabDips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("standards");
  const [showCreate, setShowCreate] = useState(false);
  const [expandedDip, setExpandedDip] = useState(null);
  const [form, setForm] = useState({ name: "", hex_code: "#000000", pantone_ref: "", tolerance_delta_e: "" });
  const [saving, setSaving] = useState(false);
  const [showCreateDip, setShowCreateDip] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [designs, setDesigns] = useState([]);
  // A lab dip STARTS as a request — colour + material + supplier + due date. The swatch and
  // delta E arrive later, when the supplier submits a round, so they are not asked for here.
  const [dipForm, setDipForm] = useState({ colour_standard_id: "", supplier_id: "", material_id: "", colourway_name: "", style_id: "", due_date: "" });
  const [materials, setMaterials] = useState([]);
  const [dipFilter, setDipFilter] = useState("all");
  const [roundForm, setRoundForm] = useState({ swatch_url: "", delta_e: "", notes: "" });
  const [lightSource, setLightSource] = useState("D65");

  const load = () => {
    Promise.all([
      api.get("/colour-standards").then((r) => setColours(r.data)).catch(() => {}),
      api.get("/lab-dips").then((r) => setLabDips(r.data)).catch(() => {}),
      api.get("/material-masters").then((r) => setMaterials(r.data)).catch(() => setMaterials([])),
      api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {}),
      api.get("/designs").then((r) => setDesigns(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/colour-standards", {
        colour_code: form.name,
        hex_code: form.hex_code || "",
        reference_system: form.pantone_ref || "Pantone",
        tolerance_delta_e: form.tolerance_delta_e ? parseFloat(form.tolerance_delta_e) : null,
      });
      toast.success("Colour standard created");
      setShowCreate(false);
      setForm({ name: "", hex_code: "#000000", pantone_ref: "", tolerance_delta_e: "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const createLabDip = async () => {
    setSaving(true);
    try {
      await api.post("/lab-dips", {
        colour_standard_id: dipForm.colour_standard_id,
        supplier_id: dipForm.supplier_id,
        colourway_name: dipForm.colourway_name,
        style_id: dipForm.style_id || undefined,
        material_id: dipForm.material_id || undefined,
        due_date: dipForm.due_date || "",
      });
      toast.success("Lab dip requested");
      setShowCreateDip(false);
      setDipForm({ colour_standard_id: "", supplier_id: "", material_id: "", colourway_name: "", style_id: "", due_date: "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  // SOW step 2/5 — the supplier submits a dyed swatch, opening the next round. There was no way
  // to do this from the UI at all: rounds could only be decided, never created.
  const submitRound = async (dipId) => {
    try {
      await api.post(`/lab-dips/${dipId}/rounds`, {
        swatch_url: roundForm.swatch_url || "",
        delta_e: roundForm.delta_e ? parseFloat(roundForm.delta_e) : null,
        notes: roundForm.notes || "",
      });
      toast.success("Round submitted");
      setRoundForm({ swatch_url: "", delta_e: "", notes: "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const updateLabDipStatus = async (id, status) => {
    try {
      await api.patch(`/lab-dips/${id}`, { status });
      toast.success(`Lab dip ${status}`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const submitRoundDecision = async (dipId, roundNumber, decision, notes) => {
    try {
      await api.post(`/lab-dips/${dipId}/rounds/${roundNumber}/decision`, {
        decision, notes: notes || "", light_source: lightSource,
      });
      toast.success(`Round ${roundNumber} ${decision}`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const visibleDips = dipFilter === "all" ? labDips : labDips.filter((d) => d.status === dipFilter);

  // Check for unapproved lab dips that would block PO creation
  const unapprovedDips = labDips.filter((d) => d.status !== "approved");

  return (
    <div className="p-10 max-w-6xl mx-auto">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-zinc-400">PLM</p>
          <h1 className="font-heading text-3xl font-bold mt-2 tracking-tight">Colour Management</h1>
          <p className="text-zinc-500 mt-2 text-sm">Colour standards, swatches and lab dip tracking.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowCreateDip(true)}
            className="inline-flex items-center gap-2 bg-zinc-100 text-zinc-800 px-5 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-200 transition-colors">
            <Plus size={15} /> Request lab dip
          </button>
          <button onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 bg-zinc-950 text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors">
            <Plus size={15} /> Add colour
          </button>
        </div>
      </div>

      {/* PO Gate Warning */}
      {unapprovedDips.length > 0 && tab === "labdips" && (
        <div className="mt-4 flex items-center gap-3 px-4 py-3 rounded-lg text-sm bg-amber-50 text-amber-800 border border-amber-200">
          <span className="font-medium">PO Gate:</span>
          <span>{unapprovedDips.length} lab dip(s) pending approval. Purchase orders cannot be created for suppliers with unapproved colourways.</span>
        </div>
      )}

      {/* Tabs */}
      <div className="mt-8 flex gap-1 border-b border-zinc-200">
        <button onClick={() => setTab("standards")}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px ${tab === "standards" ? "border-zinc-950 text-zinc-950" : "border-transparent text-zinc-400 hover:text-zinc-600"}`}>
          Colour Standards
        </button>
        <button onClick={() => setTab("labdips")}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px ${tab === "labdips" ? "border-zinc-950 text-zinc-950" : "border-transparent text-zinc-400 hover:text-zinc-600"}`}>
          Lab Dips
        </button>
      </div>

      {loading ? (
        <div className="mt-10 text-sm text-zinc-400">Loading...</div>
      ) : tab === "standards" ? (
        colours.length === 0 ? (
          <div className="mt-10 border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400">
            <Palette size={26} className="mx-auto mb-3" />
            <p className="text-sm">No colour standards yet.</p>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {colours.map((c) => (
              <div key={c.id} className="border border-zinc-200 bg-white rounded-lg overflow-hidden hover:shadow-sm transition-shadow">
                <div className="h-20 flex items-center justify-center" style={{ backgroundColor: c.hex_code || "#e4e4e7" }}>
                  <span className="text-2xl font-mono font-bold" style={{ color: c.hex_code ? "#fff" : "#71717a", textShadow: c.hex_code ? "0 1px 3px rgba(0,0,0,0.4)" : "none" }}>{(c.colour_code || "").slice(0, 3)}</span>
                </div>
                <div className="p-4">
                  <p className="text-sm font-medium">{c.colour_code}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-zinc-400">{c.reference_system || "Pantone"}</span>
                    {c.pantone_ref && <span className="text-[10px] bg-zinc-100 text-zinc-600 px-1.5 py-0.5 rounded font-mono">{c.pantone_ref}</span>}
                  </div>
                  {c.hex_code && <p className="text-[10px] text-zinc-400 mt-0.5 font-mono">{c.hex_code}</p>}
                  {c.tolerance_delta_e != null && (
                    <div className="mt-1.5">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] text-zinc-400">ΔE:</span>
                        <div className="flex-1 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${c.tolerance_delta_e <= 1 ? "bg-emerald-500" : c.tolerance_delta_e <= 2 ? "bg-amber-500" : "bg-red-500"}`}
                            style={{ width: `${Math.min((c.tolerance_delta_e / 5) * 100, 100)}%` }} />
                        </div>
                        <span className="text-[10px] font-medium text-zinc-600">{c.tolerance_delta_e}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        <>
        {/* SOW step 6 — TRACK. The submission queue is filterable by where each dip is waiting. */}
        <div className="mt-6 flex gap-1.5 flex-wrap">
          {["all", "requested", "submitted", "approved", "rejected"].map((f) => {
            const n = f === "all" ? labDips.length : labDips.filter((d) => d.status === f).length;
            return (
              <button key={f} onClick={() => setDipFilter(f)}
                className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${
                  dipFilter === f ? "bg-zinc-950 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}>
                {f} <span className="opacity-60">{n}</span>
              </button>
            );
          })}
        </div>
        {visibleDips.length === 0 ? (
          <div className="mt-10 border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400">
            <Palette size={26} className="mx-auto mb-3" />
            <p className="text-sm">No lab dip submissions yet.</p>
          </div>
        ) : (
          <div className="mt-6 space-y-3">
            {visibleDips.map((d) => (
              <div key={d.id} className="border border-zinc-200 bg-white rounded-lg overflow-hidden">
                <div className="flex items-center px-6 py-4 cursor-pointer hover:bg-zinc-50" onClick={() => setExpandedDip(expandedDip === d.id ? null : d.id)}>
                  <div className="w-8 h-8 rounded border border-zinc-200 bg-zinc-100 flex items-center justify-center mr-4">
                    <Palette size={14} className="text-zinc-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">{d.colourway_name || `Lab Dip ${d.id.slice(0, 8)}`}</p>
                    <p className="text-xs text-zinc-400">Standard: {d.colour_standard_id?.slice(0, 8)} | Supplier: {d.supplier_id?.slice(0, 8)}</p>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium mr-3 ${STATUS_STYLE[d.status] || STATUS_STYLE.pending}`}>{d.status}</span>
                  {expandedDip === d.id ? <ChevronDown size={14} className="text-zinc-400" /> : <ChevronRight size={14} className="text-zinc-400" />}
                </div>
                {expandedDip === d.id && (
                  <div className="px-6 pb-4 border-t border-zinc-100 pt-3">
                    {/* Swatch Preview */}
                    {d.swatch_url && (
                      <div className="mb-3">
                        <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-1">Swatch</p>
                        <img src={d.swatch_url} alt="Swatch" className="w-20 h-20 rounded border border-zinc-200 object-cover" />
                      </div>
                    )}
                    {/* Style Linkage */}
                    {d.style_id && (
                      <div className="mb-3">
                        <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-1">Linked Design</p>
                        <p className="text-xs text-zinc-600">{designs.find((ds) => ds.id === d.style_id)?.title || d.style_id?.slice(0, 8)}</p>
                      </div>
                    )}
                    <h4 className="text-xs uppercase tracking-[0.15em] text-zinc-400 mb-2">Round History</h4>
                    {(d.rounds || []).length === 0 ? (
                      <p className="text-xs text-zinc-400">No rounds recorded yet.</p>
                    ) : (
                      <div className="space-y-2">
                        {(d.rounds || []).map((r, i) => (
                          <div key={i} className="flex items-center gap-3 text-sm">
                            <span className="text-xs text-zinc-400 w-16">Round {r.round || r.round_number || i + 1}</span>
                            {r.swatch_url ? (
                              <img src={r.swatch_url} alt={`Round ${i + 1}`} className="w-6 h-6 rounded border border-zinc-200 object-cover" />
                            ) : (
                              <div className="w-6 h-6 rounded border" style={{ backgroundColor: r.submitted_hex || "#e4e4e7" }} />
                            )}
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_STYLE[r.status || r.decision] || STATUS_STYLE.pending}`}>{r.status || r.decision || "pending"}</span>
                            {r.delta_e != null && <span className="text-[10px] text-zinc-400">ΔE: {r.delta_e}</span>}
                            {r.notes && <span className="text-xs text-zinc-500 truncate max-w-[120px]">{r.notes}</span>}
                            {(r.status === "pending" || r.decision === "pending" || (!r.status && !r.decision)) && (
                              <div className="flex gap-1 ml-auto">
                                <button onClick={() => submitRoundDecision(d.id, r.round || r.round_number || i + 1, "approved", "")}
                                  className="px-2 py-0.5 bg-emerald-50 text-emerald-800 rounded text-[10px] hover:bg-emerald-100">Approve</button>
                                <button onClick={() => submitRoundDecision(d.id, r.round || r.round_number || i + 1, "resubmit", "")}
                                  title="Send back to the supplier for another round"
                                  className="px-2 py-0.5 bg-amber-50 text-amber-800 rounded text-[10px] hover:bg-amber-100">Resubmit</button>
                                <button onClick={() => submitRoundDecision(d.id, r.round || r.round_number || i + 1, "rejected", "")}
                                  className="px-2 py-0.5 bg-red-50 text-red-700 rounded text-[10px] hover:bg-red-100">Reject</button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* SOW step 2/5 — the supplier's submission. Evaluations are judged under a
                        stated light source: a dip that matches in D65 and fails in TL84 is the
                        everyday case, so the decision records which was used. */}
                    <div className="mt-4 pt-3 border-t border-zinc-100">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-xs uppercase tracking-[0.15em] text-zinc-400">Submit round {(d.rounds || []).length + 1}</h4>
                        <label className="text-[10px] text-zinc-400 flex items-center gap-1">
                          Light source
                          <select value={lightSource} onChange={(e) => setLightSource(e.target.value)}
                            className="border border-zinc-200 rounded px-1.5 py-0.5 text-[10px]">
                            {["D65", "TL84", "CWF", "UV", "Daylight"].map((l) => <option key={l} value={l}>{l}</option>)}
                          </select>
                        </label>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <input value={roundForm.swatch_url} onChange={(e) => setRoundForm({ ...roundForm, swatch_url: e.target.value })}
                          placeholder="Swatch image URL" className="border border-zinc-200 rounded-md px-2 py-1.5 text-xs" />
                        <input value={roundForm.delta_e} onChange={(e) => setRoundForm({ ...roundForm, delta_e: e.target.value })}
                          placeholder="Delta E" type="number" step="0.1" className="border border-zinc-200 rounded-md px-2 py-1.5 text-xs" />
                        <button onClick={() => submitRound(d.id)}
                          className="px-3 py-1.5 bg-zinc-950 text-white rounded-md text-xs hover:bg-zinc-800">Submit round</button>
                      </div>
                    </div>
                    {d.status === "pending" && (
                      <div className="flex gap-2 mt-4">
                        <button onClick={() => submitRound(d.id)}
                          className="px-4 py-1.5 bg-blue-50 text-blue-800 rounded-md text-xs font-medium hover:bg-blue-100">Submit Round</button>
                        <button onClick={() => updateLabDipStatus(d.id, "approved")}
                          className="px-4 py-1.5 bg-emerald-50 text-emerald-800 rounded-md text-xs font-medium hover:bg-emerald-100">Approve</button>
                        <button onClick={() => updateLabDipStatus(d.id, "rejected")}
                          className="px-4 py-1.5 bg-red-50 text-red-700 rounded-md text-xs font-medium hover:bg-red-100">Reject</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        </>
      )}

      {showCreateDip && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setShowCreateDip(false)}>
          <div className="bg-white rounded-lg p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg">New lab dip submission</h2>
              <button onClick={() => setShowCreateDip(false)} className="p-1.5 hover:bg-zinc-100 rounded-md"><X size={16} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-zinc-500 mb-1">Colour Standard</label>
                <select value={dipForm.colour_standard_id} onChange={(e) => setDipForm({ ...dipForm, colour_standard_id: e.target.value })}
                  className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950">
                  <option value="">Select colour standard...</option>
                  {colours.map((c) => <option key={c.id} value={c.id}>{c.colour_code}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-zinc-500 mb-1">Supplier</label>
                <select value={dipForm.supplier_id} onChange={(e) => setDipForm({ ...dipForm, supplier_id: e.target.value })}
                  className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950">
                  <option value="">Select supplier...</option>
                  {suppliers.map((s) => <option key={s.id} value={s.id}>{s.company_name || s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-zinc-500 mb-1">Style / Design (for PO gate)</label>
                <select value={dipForm.style_id} onChange={(e) => setDipForm({ ...dipForm, style_id: e.target.value })}
                  className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950">
                  <option value="">Select style...</option>
                  {designs.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
                </select>
              </div>
              <input value={dipForm.colourway_name} onChange={(e) => setDipForm({ ...dipForm, colourway_name: e.target.value })} placeholder="Colourway name"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <div>
                <label className="block text-xs font-medium text-zinc-500 mb-1">Material being dyed</label>
                <select value={dipForm.material_id} onChange={(e) => setDipForm({ ...dipForm, material_id: e.target.value })}
                  className="w-full border border-zinc-200 rounded-md px-3 py-2 text-sm">
                  <option value="">Select material (optional)</option>
                  {materials.map((m) => <option key={m.id} value={m.id}>{m.name}{m.material_type ? ` — ${m.material_type}` : ""}</option>)}
                </select>
                <label className="block text-xs font-medium text-zinc-500 mb-1 mt-3">Due date</label>
                <input type="date" value={dipForm.due_date} onChange={(e) => setDipForm({ ...dipForm, due_date: e.target.value })}
                  className="w-full border border-zinc-200 rounded-md px-3 py-2 text-sm" />
              </div>
              <button onClick={createLabDip} disabled={!dipForm.colour_standard_id || !dipForm.supplier_id || saving}
                className="w-full bg-zinc-950 text-white py-3 rounded-md font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
                {saving ? "Requesting..." : "Request lab dip"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-lg p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg">Add colour standard</h2>
              <button onClick={() => setShowCreate(false)} className="p-1.5 hover:bg-zinc-100 rounded-md"><X size={16} /></button>
            </div>
            <div className="space-y-4">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Colour name (e.g. Midnight Navy)"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <div className="flex gap-3">
                <input type="color" value={form.hex_code} onChange={(e) => setForm({ ...form, hex_code: e.target.value })}
                  className="w-12 h-10 rounded border border-zinc-300 cursor-pointer" />
                <input value={form.hex_code} onChange={(e) => setForm({ ...form, hex_code: e.target.value })} placeholder="#000000"
                  className="flex-1 border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              </div>
              <input value={form.pantone_ref} onChange={(e) => setForm({ ...form, pantone_ref: e.target.value })} placeholder="Pantone reference (optional)"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <input value={form.tolerance_delta_e} onChange={(e) => setForm({ ...form, tolerance_delta_e: e.target.value })} placeholder="Tolerance ΔE (e.g. 1.5)" type="number" step="0.1"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <button onClick={create} disabled={!form.name || saving}
                className="w-full bg-zinc-950 text-white py-3 rounded-md font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
                {saving ? "Creating..." : "Add colour"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
